import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';

export default function Annuities() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="annuities" />
          {/* Header */}
          <div className="text-center mb-12 pt-4"> {/* Added padding top */}
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Annuities</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Securing Guaranteed Income for Life or a Specific Term
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is an Annuity? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is an Annuity?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                An annuity is a contract between you and a life insurance company. In exchange for a lump-sum payment (or a series of payments), the insurance company guarantees to provide you with regular income payments for a specified period or, in the case of a life annuity, for the rest of your life.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                Annuities are primarily used to generate a predictable income stream, often during retirement, providing security against outliving your savings (longevity risk) and market volatility.
              </Text>
            </Card>

            {/* How It Works */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How Annuities Work</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Purchase:</strong> You purchase the annuity using funds from registered sources (like an RRSP or RRIF) or non-registered savings. The amount of income you receive depends on the purchase amount, your age (and potentially gender), prevailing interest rates at the time of purchase, and the type of annuity chosen.
                </li>
                <li>
                  <strong>Types of Annuities:</strong>
                    <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                      <li><strong>Life Annuity:</strong> Pays income for as long as you live. Payments stop upon death unless a guarantee period or joint life option is included. Offers maximum longevity protection.</li>
                      <li><strong>Term Certain Annuity:</strong> Pays income for a fixed number of years (e.g., 10, 15, 20 years). If you die before the term ends, payments continue to your beneficiary or estate until the end of the term.</li>
                      <li><strong>Joint Life Annuity:</strong> Pays income as long as either you or your spouse/partner is alive.</li>
                    </ul>
                </li>
                <li>
                  <strong>Guarantee Periods (Optional):</strong> A life annuity can include a guarantee period (e.g., 10 years). If you die within that period, payments continue to your beneficiary for the remainder of the guarantee period.
                </li>
                <li>
                  <strong>Income Payments:</strong> You receive regular, guaranteed payments (e.g., monthly) according to the contract terms.
                </li>
                <li>
                  <strong>Taxation:</strong> The taxation of annuity payments depends on the source of funds used for purchase. Payments from annuities bought with registered funds (RRSP/RRIF) are fully taxable. For non-registered annuities, only the interest portion of each payment is taxed.
                </li>
              </ol>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Guaranteed Income:</strong> Provides a predictable income stream you cannot outlive (life annuity).</li>
                <li><strong>Security & Peace of Mind:</strong> Eliminates investment risk and longevity risk for the portion of assets used.</li>
                <li><strong>Simplicity:</strong> "Set it and forget it" income solution, requires no ongoing investment decisions.</li>
                <li><strong>Potential Creditor Protection:</strong> Annuity payments may offer some protection from creditors depending on structure and jurisdiction.</li>
                <li><strong>Estate Planning Certainty (Term Certain):</strong> Guarantees payments for a set period, even if the annuitant passes away early.</li>
              </ul>
            </Card>

            {/* Considerations */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Considerations</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Important factors to evaluate:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Irrevocability & Lack of Flexibility:</strong> Once purchased, you generally cannot change the terms or access the lump sum. Your capital is locked in.</li>
                <li><strong>Interest Rate Sensitivity:</strong> Annuity payout rates are heavily influenced by interest rates at the time of purchase. Buying when rates are low locks in lower income for life.</li>
                <li><strong>Inflation Risk:</strong> Fixed annuity payments lose purchasing power over time due to inflation unless an indexing feature (which significantly increases cost) is included.</li>
                <li><strong>No Capital Growth Potential:</strong> You give up the potential for market growth on the capital used to buy the annuity.</li>
                <li><strong>Mortality Risk (Life Annuity without Guarantee):</strong> If you die early in a basic life annuity, the insurance company keeps the remaining capital, and payments cease.</li>
                <li><strong>Complexity of Options:</strong> Features like indexing, guarantee periods, and joint life options affect the payout amount and require careful consideration.</li>
              </ul>
            </Card>

            {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Are Annuities Right for You?</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Annuities are often suitable for:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Retirees seeking maximum income security and predictability for a portion of their savings.</li>
                <li>Individuals concerned about outliving their retirement funds (longevity risk).</li>
                <li>Risk-averse individuals who prioritize guaranteed income over potential market growth.</li>
                <li>People looking to cover essential fixed expenses in retirement with a guaranteed income source.</li>
                <li>As part of a diversified retirement income strategy, often complementing RRIFs or other investments.</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Secure Your Retirement Income Stream</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Explore how annuities can provide guaranteed income and peace of mind in retirement. Let's analyze if an annuity fits within your overall financial plan.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Discuss Annuity Options
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
        <BookingWidget /> // Removed onClose prop
      )}
    </MarketingLayout>
  );
}
